export const TASK_STATUS = {
  AVAILABLE: "available",
  CLAIMED: "claimed",
  COMPLETED: "completed",
  EXPIRED: "expired",
  DRAFT: "draft",
} as const;

export type TaskStatusValue = (typeof TASK_STATUS)[keyof typeof TASK_STATUS];

export const RESULT_STATUS = {
  DRAFT: "draft",
  SUBMITTED: "submitted",
} as const;

export type ResultStatusValue = (typeof RESULT_STATUS)[keyof typeof RESULT_STATUS];

export interface AuditUseCase {
  id: string;
  name: string;
  inter_rater_count?: number;
  [key: string]: unknown;
}

export interface AuditTask {
  id: string;
  use_case_id: string;
  status: TaskStatusValue;
  assigned_to: string | null;
  priority?: number;
  created_at: string;
  [key: string]: unknown;
}

export interface AuditResult {
  id: string;
  task_id: string;
  auditor_id: string;
  status: ResultStatusValue;
  decisions?: Record<string, { decision: string }>;
  [key: string]: unknown;
}

// ----------------------------  Config Enums  ----------------------------

export const USE_CASE_CATEGORY = {
  SAFETY_GENIE: "Safety Genie",
  SAFETY_VISION: "Safety Vision",
  CUSTOM: "Custom",
} as const;

export type UseCaseCategoryValue = (typeof USE_CASE_CATEGORY)[keyof typeof USE_CASE_CATEGORY];

export const DATA_SOURCE_TYPE = {
  TEXT_ONLY: "text_only",
  SINGLE_IMAGE_TEXT: "single_image_text",
  MULTI_IMAGE_TEXT: "multi_image_text",
  DOCUMENT_TEXT: "document_text",
  AUDIO_TEXT: "audio_text",
  VIDEO_TEXT: "video_text",
} as const;

export type DataSourceTypeValue = (typeof DATA_SOURCE_TYPE)[keyof typeof DATA_SOURCE_TYPE];

export const DATA_SOURCE_LABELS: Record<DataSourceTypeValue, string> = {
  text_only: "Text only",
  single_image_text: "Single image + text",
  multi_image_text: "Multiple images + text",
  document_text: "Document + text",
  audio_text: "Audio + text",
  video_text: "Video + text",
};

export const COMPARISON_MODE = {
  SIMPLE: "simple",
  OVERRIDE: "override",
} as const;

export type ComparisonModeValue = (typeof COMPARISON_MODE)[keyof typeof COMPARISON_MODE];

// Where a sampling run draws a use case's records from. The wire field is
// ``ingestion_source_type`` (see UseCaseConfig); these are its codes.
export const INGESTION_SOURCE_TYPE = {
  BATCH: "batch",
  TABLE: "table",
  S3_PATH: "s3_path",
} as const;

export type IngestionSourceTypeValue =
  (typeof INGESTION_SOURCE_TYPE)[keyof typeof INGESTION_SOURCE_TYPE];

export const INGESTION_SOURCE_LABELS: Record<IngestionSourceTypeValue, string> = {
  batch: "Batch",
  table: "Table",
  s3_path: "S3 path",
};

export const FIELD_TYPE = {
  TEXT_INPUT: "text_input",
  DROPDOWN_SINGLE: "dropdown_single",
  DROPDOWN_MULTI: "dropdown_multi",
  IMAGE_VIEWER: "image_viewer",
  DOCUMENT_VIEWER: "document_viewer",
  CHECKBOX: "checkbox",
  NUMERIC: "numeric",
  AUDIO_PLAYER: "audio_player",
  VIDEO_PLAYER: "video_player",
} as const;

export type FieldTypeValue = (typeof FIELD_TYPE)[keyof typeof FIELD_TYPE];

export const FIELD_TYPE_LABELS: Record<FieldTypeValue, string> = {
  text_input: "Text input",
  dropdown_single: "Dropdown (single)",
  dropdown_multi: "Dropdown (multi)",
  image_viewer: "Image viewer",
  document_viewer: "Document viewer",
  checkbox: "Checkbox",
  numeric: "Numeric",
  audio_player: "Audio player",
  video_player: "Video player",
};

export const OPTION_FIELD_TYPES: FieldTypeValue[] = [
  FIELD_TYPE.DROPDOWN_SINGLE,
  FIELD_TYPE.DROPDOWN_MULTI,
];

export const MEDIA_FIELD_TYPES: FieldTypeValue[] = [
  FIELD_TYPE.IMAGE_VIEWER,
  FIELD_TYPE.DOCUMENT_VIEWER,
  FIELD_TYPE.AUDIO_PLAYER,
  FIELD_TYPE.VIDEO_PLAYER,
];

export const CLAIM_EXPIRY_OPTIONS = [
  { value: 30, label: "30 minutes" },
  { value: 60, label: "1 hour" },
  { value: 120, label: "2 hours" },
  { value: 240, label: "4 hours" },
  { value: 480, label: "8 hours" },
];

// ----------------------------  Config Interfaces  ----------------------------

export interface FormConfigField {
  id: string;
  type: FieldTypeValue;
  label: string;
  options: string[];
  mandatory: boolean;
  help_text: string;
  display_order: number;
  default_value: string;
  multiline: boolean;
  char_limit: number | null;
  min: number | null;
  max: number | null;
  step: number | null;
  allow_other: boolean;
}

export interface ChecklistItem {
  id: string;
  label: string;
  display_order: number;
}

export interface ReviewSampling {
  second_pct: number;
  third_pct: number;
  second_enabled: boolean;
  third_enabled: boolean;
  second_reviewer: string;
  third_reviewer: string;
}

export function makeReviewSampling(overrides: Partial<ReviewSampling> = {}): ReviewSampling {
  return {
    second_pct: 0,
    third_pct: 0,
    second_enabled: false,
    third_enabled: false,
    second_reviewer: "",
    third_reviewer: "",
    ...overrides,
  };
}

export interface UseCaseConfig {
  id: string;
  name: string;
  description: string;
  category: UseCaseCategoryValue;
  data_source_type: DataSourceTypeValue;
  comparison_mode: ComparisonModeValue;
  // Ingestion tab. ``ingestion_source_type`` is the backend wire name for the
  // ingestion source *kind*; ``ingestion_source`` is the concrete pointer.
  ingestion_source_type: IngestionSourceTypeValue;
  ingestion_source: string;
  inter_rater_count: number;
  review_sampling: ReviewSampling;
  claim_expiry_minutes: number;
  status: "active" | "inactive";
  fields: FormConfigField[];
  checklist_items: ChecklistItem[];
  created_at: string;
  updated_at: string;
}

// ----------------------------  Config Helpers  ----------------------------

export function configUid(prefix: string): string {
  return prefix + "_" + crypto.randomUUID().replace(/-/g, "").slice(0, 12);
}

export function makeFormConfigField(overrides: Partial<FormConfigField> = {}): FormConfigField {
  return {
    id: configUid("f"),
    type: FIELD_TYPE.TEXT_INPUT,
    label: "",
    options: [],
    mandatory: false,
    help_text: "",
    display_order: 0,
    default_value: "",
    multiline: false,
    char_limit: null,
    min: null,
    max: null,
    step: null,
    allow_other: false,
    ...overrides,
  };
}

export function makeChecklistItem(overrides: Partial<ChecklistItem> = {}): ChecklistItem {
  return {
    id: configUid("cl"),
    label: "",
    display_order: 0,
    ...overrides,
  };
}

export function makeUseCaseConfig(overrides: Partial<UseCaseConfig> = {}): UseCaseConfig {
  const ts = new Date().toISOString();
  return {
    id: configUid("uc"),
    name: "",
    description: "",
    category: USE_CASE_CATEGORY.CUSTOM,
    data_source_type: DATA_SOURCE_TYPE.TEXT_ONLY,
    comparison_mode: COMPARISON_MODE.SIMPLE,
    ingestion_source_type: INGESTION_SOURCE_TYPE.BATCH,
    ingestion_source: "",
    inter_rater_count: 1,
    review_sampling: makeReviewSampling(),
    claim_expiry_minutes: 60,
    status: "inactive",
    fields: [],
    checklist_items: [],
    created_at: ts,
    updated_at: ts,
    ...overrides,
  };
}

// ----------------------------  Workspace Types  ----------------------------

export const SIMPLE_DECISIONS = ["correct", "incorrect"] as const;
export const OVERRIDE_DECISIONS = ["ai_correct", "human_correct", "both_incorrect"] as const;
export const CHECKLIST_RESPONSES = ["pass", "fail", "na"] as const;

export type SimpleDecisionValue = (typeof SIMPLE_DECISIONS)[number];
export type OverrideDecisionValue = (typeof OVERRIDE_DECISIONS)[number];
export type ChecklistResponseValue = (typeof CHECKLIST_RESPONSES)[number];

export interface MediaUrl {
  url: string;
  type: "image" | "document" | "pdf" | "audio" | "video";
  label: string;
}

export interface FieldDecision {
  decision: string;
  correction_value?: string;
  correction_note?: string;
  flagged?: boolean;
}

export interface WorkspaceTask {
  id: string;
  use_case_id: string;
  status: TaskStatusValue;
  assigned_to: string | null;
  priority: number;
  created_at: string;
  week?: string;
  incident_description?: string;
  inspection_question?: string;
  comparison_mode?: ComparisonModeValue;
  // Untransformed sample record from the backend; mapped to fields in the UI.
  raw_values?: Record<string, unknown>;
  media_urls?: MediaUrl[];
}

export interface WorkspaceResult {
  id: string;
  task_id: string;
  auditor_id: string;
  status: ResultStatusValue;
  decisions: Record<string, FieldDecision>;
  checklist_responses: Record<string, ChecklistResponseValue>;
  comments: string;
}

// ----------------------------  Permissions  ----------------------------

export const ROLE = {
  AUDITOR: "auditor",
  OWNER: "owner",
  ADMIN: "admin",
} as const;

export type RoleValue = (typeof ROLE)[keyof typeof ROLE];

// Roles that can be assigned from the UI. Admin is the root role and is
// granted out-of-band (LDAP audit-admin / server config), never from the front end.
export const ASSIGNABLE_ROLES: RoleValue[] = [ROLE.AUDITOR, ROLE.OWNER];

export const ROLE_LABELS: Record<RoleValue, string> = {
  [ROLE.AUDITOR]: "Auditor",
  [ROLE.OWNER]: "Program Owner",
  [ROLE.ADMIN]: "Admin",
};

export interface PermissionsMember {
  login: string;
  name: string;
  role: RoleValue;
  assigned_use_case_ids: string[];
  // Elevated Admin: authorized on every use case implicitly (server-resolved).
  elevated?: boolean;
}

// ----------------------------  Weekly Auditor Assignment  ----------------------------

export interface WeeklyAssignmentAllocation {
  login: string;
  name: string;
  selected: boolean;
  percent: number;
}

export interface WeeklyAssignmentConfig {
  use_case_id?: string;
  week: string;
  apply_to_all_weeks: boolean;
  allocations: WeeklyAssignmentAllocation[];
}
